/* ==========================================================================
   Featured collection - fractional columns
   Separate custom element: <m-featured-collection-cols>
   The original featured-collection.js / <m-featured-collection> is untouched.

   Scrolling model
   ---------------
   The plain track is a real native scroller now (overflow-x: auto in the CSS),
   so touch swipe and trackpad swipe are handled by the browser on every
   breakpoint, complete with the platform's own momentum. This file only adds
   what a browser cannot: click-and-drag for a plain mouse, with inertia so the
   row keeps gliding after the button is released instead of stopping dead.

   When the slider is on, Swiper is put into free mode with momentum so it
   behaves the same way rather than stepping card by card.

   Edge bleed is CSS only. Swiper reads its viewport width at init and on
   resize, so the negative margin on .m-fc-cols__wrapper is already part of its
   own measurements and nothing has to be recalculated here.
   ========================================================================== */

if (!customElements.get("m-featured-collection-cols")) {
  class MFeaturedCollectionCols extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        loadMoreBtn: "[data-load-more]",
        loadMoreBtnWrapper: ".m-featured-collection__button",
        productsContainer: "[data-products-container]",
        products: [".m-product-card"],
        soldNumber: ["[data-sold-number]"],
        availableNumber: ["[data-available-number]"],
        countDown: "[data-flashsale-countdown]",
        slideControls: ".m-slider-controls",
        slideContainer: ".m-fc-cols__wrapper",
      };
      this.domNodes = queryDomNodes(this.selectors, this);

      this.buttonType = this.dataset.buttonType;
      this.infiniteLoad = this.dataset.infiniteLoad;
      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.enableFlashsale = this.dataset.enableFlashsale === "true";
      this.enableCountdown = this.dataset.enableCountdown === "true";
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.revealNextSlide = this.dataset.revealNextSlide !== "false";
      this.edgeBleed = this.dataset.edgeBleed === "true";

      /* Swipe / drag. Default to on so an older cached section still swipes. */
      this.enableSwipe = this.dataset.enableSwipe !== "false";
      this.enableTrackpad = this.dataset.enableTrackpad !== "false";
      this.scrollSnap = this.dataset.scrollSnap || "none";
      this.freeScroll = this.scrollSnap === "none";

      /* `this.id` is a reflected DOM property, so writing the section id to it
         would rewrite the element's id attribute. Keep it on its own key. */
      this.sectionId = this.dataset.id;

      /* ---- Fractional column counts. parseFloat, never parseInt ---- */
      this.itemsDesktop = this.toNumber(this.dataset.itemsDesktop, 4);
      this.itemsLaptop = this.toNumber(this.dataset.itemsLaptop, this.itemsDesktop);
      this.itemsTablet = this.toNumber(this.dataset.itemsTablet, this.itemsLaptop);
      this.itemsMobile = this.toNumber(this.dataset.itemsMobile, 2);
      this.gap = this.toNumber(this.dataset.gap, 30, true);
      this.gapMobile = this.toNumber(this.dataset.gapMobile, 16, true);

      /* A disabled reveal option always resolves to whole, full-width cards.
         Liquid already sends whole values; this guard also protects sections
         rendered from cached or older markup. */
      if (!this.revealNextSlide) {
        this.itemsDesktop = Math.max(1, Math.floor(this.itemsDesktop));
        this.itemsLaptop = Math.max(1, Math.floor(this.itemsLaptop));
        this.itemsTablet = Math.max(1, Math.floor(this.itemsTablet));
        this.itemsMobile = Math.max(1, Math.floor(this.itemsMobile));
        this.setPeekVars();
      }

      // Back-compat with the original data-items attribute
      this.items = this.itemsDesktop;

      this.hasFraction = [this.itemsDesktop, this.itemsLaptop, this.itemsTablet, this.itemsMobile].some(
        (value) => value % 1 !== 0
      );

      this.initByScreenSize();

      /* The plain track exists on every breakpoint where the slider is off,
         and on mobile when the slider is disabled there. Binding is idempotent
         and self-guarding, so it is safe to call from all of these paths. */
      this.initDragScroll();

      document.addEventListener("matchMobile", () => {
        this.initByScreenSize();
        this.initDragScroll();
        this.refreshDraggable();
      });
      document.addEventListener("unmatchMobile", () => {
        this.initByScreenSize();
        this.initDragScroll();
        this.refreshDraggable();
      });

      if (this.enableFlashsale) this.initFlashSale();

      this.canLoad = true;
      this.currentPage = 1;
      this.spinner = typeof spinner === "function" ? spinner() : null;

      if (!this.enableSlider && this.buttonType === "load" && this.infiniteLoad === "true") {
        this.initInfiniteLoad();
      }
      if (!this.enableSlider && this.buttonType === "load") {
        this.initLoadMore();
      }
      if (
        MinimogTheme.config.mqlMobile &&
        this.mobileDisableSlider &&
        this.buttonType === "load" &&
        this.infiniteLoad === "true"
      ) {
        this.initInfiniteLoad();
      }
      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider && this.buttonType === "load") {
        this.initLoadMore();
      }

      document.addEventListener("matchMobile", () => {
        if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider && this.buttonType === "load") {
          this.initLoadMore();
        }
        if (
          MinimogTheme.config.mqlMobile &&
          this.mobileDisableSlider &&
          this.buttonType === "load" &&
          this.infiniteLoad === "true"
        ) {
          this.initInfiniteLoad();
        }
      });
    }

    disconnectedCallback() {
      this.stopMomentum && this.stopMomentum();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onResize) window.removeEventListener("resize", this.onResize);
    }

    toNumber(value, fallback, allowZero) {
      const parsed = parseFloat(value);
      if (isNaN(parsed)) return fallback;
      if (parsed < 0) return fallback;
      if (parsed === 0 && !allowZero) return fallback;
      return parsed;
    }

    /* Visible gap count for a given column count:
       whole N  -> N - 1 gaps
       N.x      -> floor(N) gaps, because the peeking card has a gap before it */
    gapCount(items) {
      return items % 1 === 0 ? Math.max(0, items - 1) : Math.floor(items);
    }

    /* Only needed when reveal is turned off against stale cached markup.
       It rewrites the inline custom properties the section printed, and drops
       the peek and slider-bleed classes, because with whole cards there is no
       partial card left to bleed off the edge. */
    setPeekVars() {
      const section = this.closest(".m-section") || this;
      const map = {
        "--items-desktop": this.itemsDesktop,
        "--items-laptop": this.itemsLaptop,
        "--items-tablet": this.itemsTablet,
        "--items-mobile": this.itemsMobile,
        "--gaps-desktop": this.gapCount(this.itemsDesktop),
        "--gaps-laptop": this.gapCount(this.itemsLaptop),
        "--gaps-tablet": this.gapCount(this.itemsTablet),
        "--gaps-mobile": this.gapCount(this.itemsMobile),
      };
      Object.keys(map).forEach((key) => section.style.setProperty(key, map[key]));
      ["desktop", "laptop", "tablet", "mobile"].forEach((device) => {
        this.classList.remove("m-fc-cols--peek-" + device);
        this.classList.remove("m-fc-cols--sbleed-" + device);
      });
    }

    /* Swiper needs its own classes present; the plain track must not have them
       or Swiper's stylesheet fights the flex layout. */
    setSliderMarkup(enable) {
      const wrapper = this.querySelector(".m-fc-cols__wrapper");
      const inner = this.querySelector(".m-fc-cols__inner");
      if (!wrapper || !inner) return;
      const items = inner.querySelectorAll(".m-fc-cols__item");

      if (enable) {
        wrapper.classList.add("swiper-container");
        inner.classList.add("swiper-wrapper");
        items.forEach((item) => item.classList.add("swiper-slide"));
      } else {
        wrapper.classList.remove("swiper-container");
        inner.classList.remove("swiper-wrapper");
        inner.removeAttribute("style");
        items.forEach((item) => {
          item.classList.remove("swiper-slide");
          item.removeAttribute("style");
        });
      }
    }

    initByScreenSize() {
      if (!this.enableSlider) return;
      const { slideControls } = queryDomNodes(this.selectors, this);

      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider) {
        slideControls && slideControls.classList.add("m:hidden");
        if (this.onWheel) {
          const target = this.querySelector(".m-fc-cols__wrapper");
          target && target.removeEventListener("wheel", this.onWheel);
          this.trackpadBound = false;
          this.onWheel = null;
        }
        if (this.slider && typeof this.slider.destroy === "function") {
          this.slider.destroy(false, true);
        }
        this.slider = null;
        this.swiper = null;
        this.setSliderMarkup(false);
      } else {
        slideControls && slideControls.classList.remove("m:hidden");
        if (!this.slider) {
          this.setSliderMarkup(true);
          this.initSlider();
        }
      }
    }

    toggleControls(swiper, controlsContainer, slidesPerView, totalSlides) {
      const perView = parseFloat(slidesPerView);
      const hasOverflow = totalSlides > perView;
      /* Never re-enable dragging the merchant turned off */
      swiper.allowTouchMove = hasOverflow && this.enableSwipe;
      if (!controlsContainer) return;
      if (hasOverflow) {
        controlsContainer.classList.remove("m:hidden");
      } else {
        controlsContainer.classList.add("m:hidden");
      }
    }

    initSlider() {
      const { slideContainer } = queryDomNodes(this.selectors, this);
      if (!slideContainer) return;

      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const wrapper = slideContainer.querySelector(".m-fc-cols__inner");
      const slideItemsLength = wrapper ? wrapper.childElementCount : 0;

      slideContainer.classList.add("swiper-container");

      /* Looping with a fractional slidesPerView is unreliable in Swiper,
         so it is disabled automatically when a peeking value is in use.
         Without loop, the peeking card stays where the merchant expects it. */
      const useLoop = this.enableFlashsale || this.hasFraction ? false : true;

      /* Free scrolling. The object form is what Swiper 7+ reads, the flat keys
         are what Swiper 6 reads, and an object is truthy for the v6 check, so
         one config covers both bundles. Momentum here is what makes a released
         swipe glide to a stop rather than stopping under the finger. */
      const freeModeConfig = this.freeScroll
        ? {
            enabled: true,
            momentum: true,
            momentumRatio: 1,
            momentumVelocityRatio: 1,
            momentumBounce: true,
            momentumBounceRatio: 0.6,
            minimumVelocity: 0.02,
            sticky: false,
          }
        : false;

      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView: this.itemsMobile,
        spaceBetween: this.gapMobile,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.freeScroll ? false : useLoop,
        loopAdditionalSlides: 2,
        watchOverflow: true,
        centeredSlides: false,
        grabCursor: this.enableSwipe,
        /* Touch swipe and mouse drag */
        allowTouchMove: this.enableSwipe,
        simulateTouch: this.enableSwipe,
        touchStartPreventDefault: false,
        touchReleaseOnEdges: true,
        resistanceRatio: 0.6,
        /* Free scrolling, both API generations */
        freeMode: freeModeConfig,
        freeModeMomentum: this.freeScroll,
        freeModeMomentumRatio: 1,
        freeModeMomentumVelocityRatio: 1,
        freeModeMomentumBounce: true,
        freeModeMomentumBounceRatio: 0.6,
        freeModeMinimumVelocity: 0.02,
        freeModeSticky: false,
        /* Trackpad two-finger swipe / wheel tilt.
           forceToAxis keeps vertical page scrolling working, releaseOnEdges
           hands scrolling back to the page once the row hits either end so
           the section cannot trap the reader mid-page. */
        mousewheel: this.enableTrackpad
          ? {
              forceToAxis: true,
              releaseOnEdges: true,
              thresholdDelta: 3,
              sensitivity: 1,
            }
          : false,
        /* Advance by whole cards so the peek is preserved after each move */
        slidesPerGroup: 1,
        normalizeSlideIndex: true,
        pagination: this.showPagination
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: this.itemsTablet,
            spaceBetween: this.gap,
          },
          1024: {
            slidesPerView: this.itemsLaptop,
            spaceBetween: this.gap,
          },
          1280: {
            slidesPerView: this.itemsDesktop,
            spaceBetween: this.gap,
          },
        },
        threshold: 2,
        on: {
          init: (swiper) => {
            /* `breakpoint` does not fire for the base range, so resolve the
               control visibility here too. */
            this.toggleControls(swiper, controlsContainer, swiper.params.slidesPerView, slideItemsLength);

            setTimeout(() => {
              const firstItem = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                const itemHeight = firstItem.clientHeight;
                controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

                prevButton && prevButton.classList.remove("m:hidden");
                nextButton && nextButton.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            this.toggleControls(swiper, controlsContainer, breakpointParams.slidesPerView, slideItemsLength);
          },
        },
      });

      if (this.slider && this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => this.slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => this.slider.slideNext());
      }

      this.swiper = slideContainer && slideContainer.swiper;
      this.initTrackpadFallback();
    }

    /* Some Swiper builds are compiled without the mousewheel module, in which
       case the mousewheel option is silently ignored. Detect that and drive
       the slider from raw wheel events instead, so trackpad swipe works on
       every build rather than only the full bundle. */
    initTrackpadFallback() {
      if (!this.enableTrackpad || !this.slider) return;
      const mw = this.slider.mousewheel;
      if (mw && (mw.enabled || typeof mw.enable === "function")) {
        if (mw.enabled) return;
        try {
          mw.enable();
          if (mw.enabled) return;
        } catch (e) {
          /* fall through to the manual handler */
        }
      }

      const target = this.querySelector(".m-fc-cols__wrapper");
      if (!target || this.trackpadBound) return;
      this.trackpadBound = true;

      /* In free mode the translate is nudged continuously so a trackpad feels
         like scrolling, rather than firing one slide step per gesture. */
      let cooling = false;
      this.onWheel = (event) => {
        if (!this.slider || this.slider.destroyed) return;
        /* Horizontal intent only, so vertical page scrolling is left alone */
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        const atStart = this.slider.isBeginning && event.deltaX < 0;
        const atEnd = this.slider.isEnd && event.deltaX > 0;
        if (atStart || atEnd) return; // release to the page at the edges

        event.preventDefault();

        if (this.freeScroll && typeof this.slider.setTranslate === "function") {
          const min = this.slider.maxTranslate();
          const max = this.slider.minTranslate();
          const next = Math.max(min, Math.min(max, this.slider.translate - event.deltaX));
          this.slider.setTransition(0);
          this.slider.setTranslate(next);
          this.slider.updateProgress(next);
          this.slider.updateActiveIndex();
          this.slider.updateSlidesClasses();
          return;
        }

        if (cooling) return;
        cooling = true;
        setTimeout(() => {
          cooling = false;
        }, 220);

        if (event.deltaX > 0) {
          this.slider.slideNext();
        } else {
          this.slider.slidePrev();
        }
      };

      target.addEventListener("wheel", this.onWheel, { passive: false });
    }

    /* ----------------------------------------------------------------------
       Plain scroll track: mouse drag with inertia

       Touch and trackpad are already native now that the track is a real
       scroller, so this handler deliberately ignores touch pointers and only
       covers the one input the browser cannot scroll with: a mouse.
       ---------------------------------------------------------------------- */
    initDragScroll() {
      if (!this.enableSwipe) return;
      const inner = this.querySelector(".m-fc-cols__inner");
      if (!inner || inner.dataset.fcDragBound === "true") return;
      inner.dataset.fcDragBound = "true";
      this.dragBound = true;
      this.track = inner;

      const state = {
        down: false,
        moved: false,
        startX: 0,
        startScroll: 0,
        lastX: 0,
        lastTime: 0,
        velocity: 0,
        raf: null,
      };
      this.dragState = state;

      /* Swiper owns the element while the slider is on, so stay out of its way */
      const isTrack = () => !inner.classList.contains("swiper-wrapper");
      const isScrollable = () => isTrack() && inner.scrollWidth - inner.clientWidth > 1;
      const maxScroll = () => Math.max(0, inner.scrollWidth - inner.clientWidth);

      this.refreshDraggable = () => {
        inner.classList.toggle("is-draggable", isScrollable());
      };
      this.refreshDraggable();

      this.onResize = () => this.refreshDraggable();
      window.addEventListener("resize", this.onResize, { passive: true });

      if (typeof ResizeObserver === "function") {
        this.resizeObserver = new ResizeObserver(() => this.refreshDraggable());
        this.resizeObserver.observe(inner);
      }

      this.stopMomentum = () => {
        if (state.raf) cancelAnimationFrame(state.raf);
        state.raf = null;
        inner.classList.remove("is-gliding");
      };

      /* Exponential decay, normalised to the frame time so a 120Hz screen
         glides the same distance as a 60Hz one. Snapping stays suspended
         until the glide settles, then the browser resolves it if the merchant
         asked for snapping at all. */
      const startMomentum = () => {
        const limit = maxScroll();
        let last = performance.now();
        inner.classList.add("is-gliding");

        const step = (now) => {
          const delta = Math.min(now - last, 50) || 16;
          last = now;
          state.velocity *= Math.pow(0.94, delta / 16);

          const next = inner.scrollLeft + state.velocity * delta;

          if (Math.abs(state.velocity) < 0.02 || next <= 0 || next >= limit) {
            inner.scrollLeft = Math.max(0, Math.min(limit, next));
            state.raf = null;
            state.velocity = 0;
            inner.classList.remove("is-gliding");
            inner.classList.remove("is-dragging");
            return;
          }

          inner.scrollLeft = next;
          state.raf = requestAnimationFrame(step);
        };

        state.raf = requestAnimationFrame(step);
      };

      inner.addEventListener("pointerdown", (event) => {
        /* Left button only, mouse or pen only, and only when there is
           somewhere to scroll. Touch keeps the platform's own scroller. */
        if (event.button !== 0 || !isScrollable()) return;
        if (event.pointerType === "touch") return;

        this.stopMomentum();
        state.down = true;
        state.moved = false;
        state.startX = event.clientX;
        state.lastX = event.clientX;
        state.lastTime = performance.now();
        state.velocity = 0;
        state.startScroll = inner.scrollLeft;
      });

      inner.addEventListener("pointermove", (event) => {
        if (!state.down) return;
        const delta = event.clientX - state.startX;
        if (!state.moved && Math.abs(delta) < 4) return; // let real clicks through
        if (!state.moved) {
          state.moved = true;
          inner.classList.add("is-dragging");
          if (inner.setPointerCapture) {
            try {
              inner.setPointerCapture(event.pointerId);
            } catch (e) {
              /* capture is a nicety, not required */
            }
          }
        }

        const now = performance.now();
        const dt = now - state.lastTime;
        if (dt > 0) {
          /* px per ms, in scroll direction. Smoothed so a single jittery
             sample cannot throw the release velocity. */
          const instant = -(event.clientX - state.lastX) / dt;
          state.velocity = state.velocity * 0.2 + instant * 0.8;
          state.lastX = event.clientX;
          state.lastTime = now;
        }

        inner.scrollLeft = state.startScroll - delta;
      });

      const endDrag = () => {
        if (!state.down) return;
        state.down = false;

        /* A stale velocity from a pause before release would fling the row
           for no reason, so anything older than 80ms is treated as a stop. */
        const idle = performance.now() - state.lastTime > 80;
        const shouldGlide = state.moved && !idle && Math.abs(state.velocity) > 0.05;

        if (shouldGlide) {
          /* Keep .is-dragging on through the glide: it is what holds snapping
             off and keeps the click that ends the drag from following a link. */
          startMomentum();
        } else {
          state.velocity = 0;
          /* Defer so the click that ends a drag is swallowed by the CSS
             pointer-events rule before it can follow a product link. */
          setTimeout(() => inner.classList.remove("is-dragging"), 0);
        }
      };

      inner.addEventListener("pointerup", endDrag);
      inner.addEventListener("pointercancel", endDrag);
      inner.addEventListener("pointerleave", endDrag);

      /* Any fresh input cancels an in-flight glide, the same way a native
         scroller stops when you touch it mid-fling. */
      inner.addEventListener("wheel", () => this.stopMomentum(), { passive: true });
      inner.addEventListener("touchstart", () => this.stopMomentum(), { passive: true });

      inner.addEventListener(
        "click",
        (event) => {
          if (state.moved) {
            event.preventDefault();
            event.stopPropagation();
            state.moved = false;
          }
        },
        true
      );
    }

    initLoadMore() {
      if (this.loadMoreBound) return;
      this.loadMoreBound = true;
      this.triggerLoad = false;
      this.totalPages = parseInt(this.dataset.totalPages);

      addEventDelegate({
        context: this,
        selector: this.selectors.loadMoreBtn,
        handler: (e) => {
          e.preventDefault();
          this.handleLoadMore();
        },
      });
    }

    initInfiniteLoad() {
      if (this.infiniteBound) return;
      this.infiniteBound = true;
      const maxPages = this.dataset.maxPages;

      window.addEventListener("scroll", () => {
        this.canLoad = this.currentPage < parseInt(maxPages);

        if (!this.canLoad) return;
        if (this.offsetTop + this.clientHeight - window.innerHeight < window.scrollY && !this.triggerLoad) {
          this.triggerLoad = true;
          this.handleLoadMore();
        }
      });
    }

    async handleLoadMore() {
      this.currentPage++;
      this.canLoad = this.currentPage < this.totalPages;
      this.toggleLoading(true);
      const collectionID = this.dataset.collectionId;
      const res = await fetchCountDown(collectionID);

      const url = this.dataset.url;
      const dataUrl = `${url}?page=${this.currentPage}&section_id=${this.sectionId}`;
      fetchCache(dataUrl).then((html) => {
        this.toggleLoading(false);
        const dom = generateDomFromString(html);
        const products = dom.querySelector(this.selectors.productsContainer);
        const oldCards = this.domNodes.productsContainer.childElementCount;
        let isAppended = false;

        if (products) {
          Array.from(products.childNodes).forEach((product) => {
            this.domNodes.productsContainer.appendChild(product);
            const check = setInterval(() => {
              if (this.domNodes.productsContainer.childElementCount > oldCards) {
                clearInterval(check);
                isAppended = true;
              }
            }, 50);
          });
        }

        /* New cards change the scrollable width, so the grab cursor state has
           to be resolved again. */
        this.refreshDraggable && this.refreshDraggable();

        if (this.enableFlashsale && res.ok && res.payload && res.payload.length) {
          const { expires_date, cdt_type, duration } = res.payload[0];
          Object.assign(this, { expires_date, cdt_type, duration });
          const check = setInterval(() => {
            if (isAppended) {
              clearInterval(check);
              const cards = this.domNodes.productsContainer.querySelectorAll(".m-product-card");
              cards.forEach((card) => {
                const content = card.querySelector(".m-product-card__content");
                const progress = card.querySelector(".m-product-sale-progress");
                if (!progress) {
                  let soldNumber;
                  if (card.dataset.soldNumber) {
                    soldNumber = card.dataset.soldNumber;
                  }
                  const component = saleProgress(res.payload[0], card.dataset.productId, soldNumber);
                  const newComponent = generateDomFromString(component);
                  content.appendChild(newComponent);
                }
              });
            }
          }, 50);
        }

        this.triggerLoad = false;

        if (!this.canLoad) {
          this.domNodes.loadMoreBtn && this.domNodes.loadMoreBtn.classList.add("m:hidden");
        }
      });
    }

    toggleLoading(status) {
      if (!this.domNodes.loadMoreBtn) return;
      if (status) {
        this.domNodes.loadMoreBtn.classList.add("m-spinner-loading");
      } else {
        this.domNodes.loadMoreBtn.classList.remove("m-spinner-loading");
      }
    }

    async initFlashSale() {
      const collectionID = this.dataset.collectionId;
      const res = await fetchCountDown(collectionID);
      if (res.ok && res.payload && res.payload.length) {
        const { expires_date, cdt_type, duration } = res.payload[0];
        Object.assign(this, { expires_date, cdt_type, duration });
        this.domNodes.products.forEach((card) => {
          const content = card.querySelector(".m-product-card__content");
          const pcardSale = card.querySelector(".m-product-sale-progress");
          let soldNumber;
          if (card.dataset.soldNumber) {
            soldNumber = card.dataset.soldNumber;
          }
          const component = saleProgress(res.payload[0], card.dataset.productId, soldNumber);
          const newComponent = generateDomFromString(component);
          if (!pcardSale) {
            content.appendChild(newComponent);
          }
        });
        this.initCountDown();
      }
    }

    initCountDown() {
      const endTime = new Date(this.expires_date).getTime();
      this.countDownTimer = new CountdownTimer(this.domNodes.countDown, Date.now(), endTime, {
        type: this.cdt_type,
        duration: this.duration,
      });
    }
  }

  customElements.define("m-featured-collection-cols", MFeaturedCollectionCols);
}
